import * as Icon from "@ant-design/icons";
import {
	Button,
	Flex,
	Layout,
	Menu,
	Modal,
	Select,
	Tag,
	Tooltip,
	theme,
} from "antd";
import type { ItemType, MenuItemType } from "antd/es/menu/interface";
import { useFlags } from "flagsmith/react";
import { useAccessLevel } from "hooks/useAccessLevel";
import { useAdvancedRecAccess } from "hooks/useAdvancedRecAccess";
import { useCompanies } from "hooks/useCompanies";
import { useFileManagerAccess } from "hooks/useFileManagerAccess";
import { usePluginAccess } from "hooks/usePluginAccess";
import { useUser } from "hooks/useUser";
import { useContext, useEffect, useState } from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { useCompanyStore } from "store/company.store";
import { companySwitchPath } from "./companySwitchPath";
import { useSidebar } from "./SidebarContext";
import { SIDER_MENU_INLINE_INDENT } from "./siderLayout";

// Walkthrough video for the new AutoRec experience.
const AUTOREC_DEMO_VIDEO_URL =
	"https://demos.equilityhq.com/demo/cmrpchqkh3wynqm57el2dteui?utm_source=link";

const AUTOREC_HINT = "AI Mapping & Matching Capabilities";
const CLASSIC_HINT = "No longer supported";

// Marks the popup a group opens on the railed sider, so the popup layout rule
// in styles/index.css reaches these menus and no other.
const SIDER_GROUP_POPUP = "sider-group-popup";

// A hint on a menu row. Both rows that carry one sit inside the Task Automations
// group (EQ-2434), where the label is always visible: in the open sider the group
// expands inline, and on the rail it opens as a popup listing the full labels.
// antd only turns `title` into a tooltip for a top-level row on the rail, so the
// hint has to come from this wrapper in both cases, and the row's `title` stays
// blank so antd does not forward it to the DOM as a second, native tooltip.
function withMenuTooltip(hint: string, label: React.ReactNode) {
	return (
		<Tooltip title={hint} placement="right">
			{label}
		</Tooltip>
	);
}

export function CompanyMenu() {
	const { data, isLoading, setLastCompany } = useCompanies();
	const { user } = useUser();
	const { accessLevel } = useAccessLevel();
	const { isPluginDisabled } = usePluginAccess();
	const { isAdvancedRecDisabled } = useAdvancedRecAccess();
	const { isFileManagerDisabled } = useFileManagerAccess();
	const flags = useFlags(["disable_services_menu"]);
	const dashboardFlag = useFlags(["hide_dashboard"]);
	const reclassifyFlag = useFlags(["disable_reclassify"]);
	const shopifyFlag = useFlags(["hide_shopify"]);
	const navigate = useNavigate();
	const match = useMatch("/company/:id/*");
	const { collapsed } = useSidebar();
	// The Sider's railed state (collapsed or narrow), as antd's Menu reads it.
	// `collapsed` above is only the stored preference and misses the narrow
	// breakpoint, where an open group would be a popup left hanging open.
	const isSiderRailed = Boolean(
		useContext(Layout._InternalSiderContext).siderCollapsed,
	);
	const [upgradeOpen, setUpgradeOpen] = useState(false);
	const [companyListOpen, setCompanyListOpen] = useState(false);

	const companyId =
		useCompanyStore((state) => state.id) ??
		user?.lastCompany?.id ??
		data?.[0]?.id ??
		null;
	const setCompany = useCompanyStore((state) => state.setCompany);

	// The company the URL names, or null when we are not on a company route.
	const urlCompanyId = match?.params.id ? Number(match.params.id) : null;

	useEffect(() => {
		if (!data) return;
		// An id the URL names is CompanyPage's to judge (see below). Checked first:
		// a no-access user has an EMPTY list, and clearing here re-opened the same
		// store ping-pong on a direct link (LANYAP-WEB-19K).
		if (companyId && companyId === urlCompanyId) return;
		if (companyId && !data.length) {
			setCompany(null);
			if (match) navigate("/");
			return;
		}
		// Heal a stale persisted company id. The store (sessionStorage
		// `company-storage`) can hold a company the current user can no longer
		// access — e.g. a former default company that was later orphaned. That
		// stale id then leaks into company-scoped requests (POST
		// companies/<id>/connection/link → 401 "no access"). If the persisted id
		// isn't in the accessible list, clear it so the fallback chain
		// (lastCompany → first accessible company) picks a valid one.
		//
		// 🔑 STALE means PERSISTED, never URL-BORNE. `CompanyPage` treats the URL
		// as the source of truth and writes `/company/:id` straight back into the
		// store, so clearing an id the URL names is a write the next render
		// undoes — the two effects then overwrite each other and re-render the
		// whole tree until React throws "Maximum update depth exceeded" (Sentry
		// LANYAP-WEB-19K). An inaccessible id in the URL is already handled where
		// it can be handled properly: `CompanyPage` sends the user back to the
		// company list on a 401/403 from the company fetch.
		if (companyId && data.length && !data.some((c) => c.id === companyId)) {
			setCompany(null);
		}
	}, [data, companyId, urlCompanyId]);

	if (!companyId) return null;
	// Gate on the SELECTED company's tier (its owning team's owner), not the
	// user's max across every team: a Pro team's company must not show
	// Advanced items because the user also sits on an Advanced team.
	const companyAccessLevel =
		data?.find((c) => c.id === companyId)?.accessLevel ?? accessLevel;
	if (!companyAccessLevel) return null;

	// When AutoRec is unavailable for this user its menu item is hidden below,
	// so classic must keep navigating directly. An upgrade prompt pointing at a
	// page they cannot reach would be a dead end.
	const autoRecAvailable = !isAdvancedRecDisabled && !isPluginDisabled;

	// Keys hidden by feature flags or plan access. They sit inside groups since
	// EQ-2434, so they are removed at every level by `pruneMenu`, not by a
	// top-level filter.
	const hiddenKeys = new Set<string>();
	if (dashboardFlag?.hide_dashboard?.enabled) hiddenKeys.add("dashboard");
	if (flags?.disable_services_menu?.enabled) hiddenKeys.add("services");
	if (!autoRecAvailable) hiddenKeys.add("advanced_reconcile");
	if (isFileManagerDisabled) hiddenKeys.add("files");
	// Shopify is the only e-commerce platform the module lists, so the flag that
	// suppresses the Shopify surface suppresses the module with it.
	if (shopifyFlag?.hide_shopify?.enabled) hiddenKeys.add("ecommerce");
	if (reclassifyFlag?.disable_reclassify?.enabled) hiddenKeys.add("reclassify");

	const menuItems = lockByAccessLevel(
		pruneMenu(
			getMenuItems(
				companyId,
				autoRecAvailable ? () => setUpgradeOpen(true) : undefined,
			),
			hiddenKeys,
		),
		companyAccessLevel,
	);

	const pathParts = match?.params["*"]?.split("/") ?? [];
	const path = pathParts[0];
	// Map 'reconciles' path (including its sub-routes like mapping-rules) to the
	// 'advanced_reconcile' (AutoRec) key for menu highlighting. The company's
	// index route renders the Analytics page, so it highlights that row.
	const menuKey =
		path === "reconciles" ? "advanced_reconcile" : path || "analytics";
	const selected = match
		? leafKeys(menuItems).find((key) => key === menuKey)
		: undefined;
	// Open the group holding the current page on load, as Project Management
	// does. Only the initial value: after that the user opens and closes groups
	// freely. Not on a railed sider.
	const selectedGroup = isSiderRailed
		? undefined
		: menuItems.find(
				(item) =>
					item &&
					"children" in item &&
					item.children?.some((child) => child?.key === selected),
			)?.key;

	const companies = data?.map((company) => ({
		label: company.alias || company.name,
		value: company.id,
	}));
	// A value with no matching option makes antd render the bare id ("491")
	// as the selected company: the URL names a company this user cannot reach,
	// or the list has not loaded yet. Show the placeholder instead.
	const selectedCompanyId = companies?.some((c) => c.value === companyId)
		? companyId
		: undefined;
	// The collapsed sider cuts the name to "T…", so hovering it names the company
	// (EQ-2410). The open sider shows the whole name and needs no tooltip, and
	// none shows over the open company list.
	const collapsedCompanyHint =
		collapsed && !companyListOpen
			? companies?.find((c) => c.value === selectedCompanyId)?.label
			: undefined;

	// Handle company switching based on current URL section
	const handleCompanyChange = (id: number) => {
		setLastCompany(id);
		// Sync the company store immediately. Company-scoped hooks (useCompany,
		// usePlaid, the QBO connect handlers, …) read the persisted store id, not
		// the route param. Switching without setting it leaves them firing against
		// the previously-selected company (e.g. POST companies/165/connection/link
		// while viewing company 260 → 401). The CompanyPage gate also syncs on
		// route change, but setting it here removes the FullLoader flash and the
		// window where children read the stale id.
		setCompany(id);
		// Check if we're currently on a company-specific URL (top section)
		if (match) {
			// Get the current path after /company/:id/
			const currentPath = match.params["*"] || "";

			// Same page under the new company, except a page naming one record
			// (a review, a rule, a reconciliation), which drops to its list.
			navigate(`/company/${id}/${companySwitchPath(currentPath)}`);
		}
		// On a company-agnostic URL (bottom section) the store update above is
		// enough — no navigation needed.
	};

	return (
		<Flex vertical gap={10}>
			<Tooltip title={collapsedCompanyHint} placement="right">
				<Flex style={companyStyle}>
					<Select
						// LYP-1072 - Allow users to search for company in top left dropdown box
						showSearch
						filterOption={(input, option) =>
							(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
						}
						variant="borderless"
						value={selectedCompanyId}
						placeholder={isLoading ? "Loading companies" : "Select a company"}
						options={companies}
						loading={isLoading}
						style={companySelectStyle}
						onChange={handleCompanyChange}
						onOpenChange={setCompanyListOpen}
					/>
				</Flex>
			</Tooltip>
			<Menu
				mode="inline"
				inlineIndent={SIDER_MENU_INLINE_INDENT}
				items={menuItems}
				selectedKeys={selected ? [selected] : []}
				defaultOpenKeys={selectedGroup ? [String(selectedGroup)] : undefined}
				style={menuStyle}
			/>
			<Modal
				open={upgradeOpen}
				onCancel={() => setUpgradeOpen(false)}
				title="Upgrade to AutoRec!"
				footer={null}
				centered
			>
				<p style={{ marginBottom: 24 }}>
					The new AutoRec has advanced AI matching and mapping features that let
					you complete your reconciliation even quicker (
					<a
						href={AUTOREC_DEMO_VIDEO_URL}
						target="_blank"
						rel="noopener noreferrer"
					>
						watch the demo
					</a>
					). Account Reconciliations is no longer supported.
				</p>
				<Flex gap={8} justify="flex-end">
					<Button
						style={classicButtonStyle}
						onClick={() => {
							setUpgradeOpen(false);
							navigate(`/company/${companyId}/reconcile`);
						}}
					>
						Continue to Account Reconciliations
					</Button>
					<Button
						type="primary"
						onClick={() => {
							setUpgradeOpen(false);
							navigate(`/company/${companyId}/reconciles`);
						}}
					>
						Take me to AutoRec
					</Button>
				</Flex>
			</Modal>
		</Flex>
	);
}

type CompanyMenuItem = ItemType<MenuItemType & { accesslevel?: number }>;

/**
 * The company section of the sidebar (EQ-2434): Platform Integrations (named
 * Settings & Integrations until EQ-2471), then the Task Automations and
 * Verify & Close groups. Human Assist is not named in the new layout, and
 * dropping a page's only menu entry was not asked for, so it stays as its own
 * row underneath.
 *
 * `accesslevel` is the plan tier a row needs. Groups carry none; their rows
 * are locked one by one (see `lockByAccessLevel`).
 */
const getMenuItems = (
	companyId: number,
	onClassicClick?: () => void,
): CompanyMenuItem[] => [
	{
		label: (
			<Link to={`/company/${companyId}/settings`} style={menuLinkStyle}>
				Platform Integrations
			</Link>
		),
		key: "settings",
		icon: <Icon.ClusterOutlined />,
		accesslevel: 0,
		title: "Platform Integrations",
	},
	{
		label: <div style={menuLinkStyle}>Task Automations</div>,
		key: "task-automations",
		popupClassName: SIDER_GROUP_POPUP,
		icon: <Icon.ThunderboltOutlined />,
		children: [
			{
				label: (
					<Link to={`/company/${companyId}/files`} style={menuLinkStyle}>
						File Converter
					</Link>
				),
				key: "files",
				icon: <Icon.BankOutlined />,
				accesslevel: 0,
				title: "File Converter",
			},
			{
				label: withMenuTooltip(
					AUTOREC_HINT,
					<Link to={`/company/${companyId}/reconciles`} style={menuLinkStyle}>
						AutoRec
					</Link>,
				),
				key: "advanced_reconcile",
				icon: <Icon.InteractionOutlined />,
				accesslevel: 0,
				title: "",
			},
			{
				label: withMenuTooltip(
					CLASSIC_HINT,
					// Clicking classic opens the upgrade modal instead of navigating. Kept as a
					// Link (rather than a span) so the href stays real for middle- and
					// modified-click, and for the menu-highlighting that keys off the route.
					<Link
						to={`/company/${companyId}/reconcile`}
						onClick={
							onClassicClick
								? (e) => {
										// A modified click is the browser's "open in a new tab/window"
										// gesture. Swallowing it would trap the user in this tab with a
										// modal they didn't ask for, so let those through untouched.
										if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
											return;
										e.preventDefault();
										onClassicClick();
									}
								: undefined
						}
						style={menuLinkStyle}
					>
						Account Reconciliations{" "}
						{/* Inline rather than a flex row, so the tag wraps into the text flow and
					    usually shares a line with "Reconciliations" instead of claiming one of
					    its own. Whether it fits depends on the rendered font, so the row itself
					    must not assume a line count — see the sidebar menu rule in
					    styles/index.css, which lets the row grow to whatever this wraps to. */}
						<Tag color="default" bordered={false} className="sider-legacy-tag">
							Legacy
						</Tag>
					</Link>,
				),
				key: "reconcile",
				icon: <Icon.InteractionOutlined />,
				accesslevel: 0,
				title: "",
			},
			{
				label: (
					<Link to={`/company/${companyId}/ecommerce`} style={menuLinkStyle}>
						E-Commerce
					</Link>
				),
				key: "ecommerce",
				icon: <Icon.ShopOutlined />,
				accesslevel: 0,
				title: "E-Commerce",
			},
			{
				label: (
					<Link to={`/company/${companyId}/reclassify`} style={menuLinkStyle}>
						Reclassify
					</Link>
				),
				key: "reclassify",
				icon: <Icon.PartitionOutlined />,
				accesslevel: 0,
				title: "Reclassify Transactions",
			},
		],
	},
	{
		label: <div style={menuLinkStyle}>Verify & Close</div>,
		key: "verify-close",
		popupClassName: SIDER_GROUP_POPUP,
		icon: <Icon.SafetyCertificateOutlined />,
		children: [
			{
				label: (
					<Link to={`/company/${companyId}/review`} style={menuLinkStyle}>
						Financial Review
					</Link>
				),
				key: "review",
				icon: <Icon.FileProtectOutlined />,
				accesslevel: 2,
				title: "Financial Review",
			},
			{
				label: (
					<Link to={`/company/${companyId}/analytics`} style={menuLinkStyle}>
						Financial Statements
					</Link>
				),
				key: "analytics",
				icon: <Icon.AreaChartOutlined />,
				accesslevel: 0,
				title: "Financial Statements",
			},
			{
				label: (
					<Link to={`/company/${companyId}/dashboard`} style={menuLinkStyle}>
						Analytics Dashboard
					</Link>
				),
				key: "dashboard",
				icon: <Icon.DashboardOutlined />,
				accesslevel: 0,
				title: "Analytics Dashboard",
			},
		],
	},
	{
		label: (
			<Link to={`/company/${companyId}/services`} style={menuLinkStyle}>
				Human Assist
			</Link>
		),
		key: "services",
		icon: <Icon.AppstoreOutlined />,
		accesslevel: 0,
		title: "Services",
	},
];

function childrenOf(item: CompanyMenuItem): CompanyMenuItem[] | undefined {
	return item && "children" in item
		? (item.children as CompanyMenuItem[] | undefined)
		: undefined;
}

/**
 * Drop every row whose key is hidden, at any depth, then any group left with
 * no rows. An empty group would render as a dropdown that opens to nothing.
 */
function pruneMenu(
	items: CompanyMenuItem[],
	hiddenKeys: ReadonlySet<string>,
): CompanyMenuItem[] {
	return items.flatMap((item) => {
		if (!item || hiddenKeys.has(String(item.key))) return [];
		const children = childrenOf(item);
		if (!children) return [item];
		const kept = pruneMenu(children, hiddenKeys);
		return kept.length ? [{ ...item, children: kept } as CompanyMenuItem] : [];
	});
}

/**
 * Disable every row above the company's plan tier and say why on hover.
 * Groups have no tier of their own and stay openable.
 */
function lockByAccessLevel(
	items: CompanyMenuItem[],
	companyAccessLevel: number,
): CompanyMenuItem[] {
	return items.map((item) => {
		const children = childrenOf(item);
		if (children) {
			return {
				...item,
				children: lockByAccessLevel(children, companyAccessLevel),
			} as CompanyMenuItem;
		}
		const required =
			item && "accesslevel" in item ? (item.accesslevel ?? 0) : 0;
		if (!item || companyAccessLevel >= required) return item;
		return {
			...item,
			disabled: true,
			label: (
				<Tooltip title={getMsgByAccessLevel(required)}>
					{"label" in item ? item.label : null}
				</Tooltip>
			),
		} as CompanyMenuItem;
	});
}

/** The keys of the rows a page can select, groups excluded. */
function leafKeys(items: CompanyMenuItem[]): string[] {
	return items.flatMap((item) => {
		const children = childrenOf(item);
		if (children) return leafKeys(children);
		return item ? [String(item.key)] : [];
	});
}

const companyStyle: React.CSSProperties = {
	background: "white",
	borderRadius: theme.getDesignToken().borderRadius,
	marginTop: 10,
};

const classicButtonStyle: React.CSSProperties = {
	background: "#fff1f0",
	borderColor: "#ffccc7",
	color: "#cf1322",
};

const companySelectStyle: React.CSSProperties = { width: "100%" };

// Lets a long menu label wrap instead of being cut off. Shared by the linked
// rows and the group titles so both lay out the same way.
const menuLinkStyle: React.CSSProperties = {
	display: "block",
	whiteSpace: "normal",
	lineHeight: 1.2,
};

const menuStyle: React.CSSProperties = {
	borderRadius: theme.getDesignToken().borderRadius,
};

function getMsgByAccessLevel(level: number) {
	switch (level) {
		case 2:
			return "Upgrade to Advanced Tier";
		case 1:
			return "Upgrade to Pro Tier";
		default:
			return "";
	}
}
